import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { NgForm } from '@angular/forms'
import { Address } from 'src/app/models/Address'
import { UserLocationRealTime } from 'src/app/models/User'
import { CompanyService } from 'src/app/services/company.service'
import { RealtimeLocationService } from 'src/app/services/realtimeLocation.service'
import { debounceTime, distinctUntilChanged } from 'rxjs/operators'
import { Subject } from 'rxjs'

@Component({
	selector: 'realtime-location',
	templateUrl: './realtime-location.component.html'
})
export class RealtimeLocationComponent implements OnInit, OnDestroy {
	@ViewChild('form') public form: NgForm
	public companyId: string = localStorage.getItem('companyId')
	public address: Address = new Address()
	public hasUsersWithLocationRealTime: boolean = true
	public selectedData: UserLocationRealTime = null
	private usersWithLocationRealtime: Array<UserLocationRealTime> = []
	private intervalLocation: NodeJS.Timer
	private map: google.maps.Map = null
	private markers: Array<google.maps.Marker> = []
	private lastAddresses = {
		lat: null,
		lng: null
	}
	public addressSelectedUser: string = '...'
	private fullScreenChangeHandler: () => void
	private iconBaseFolder: string = 'assets/images/'
	public searchUser: Subject<string> = new Subject<string>()
	public searchUserValue: string = ''
	public searchedUserNotFound = false
	public filteredUsers: Array<UserLocationRealTime> = []
	public usuariosLogados: number = 0

	constructor(
		public realtimeLocationService: RealtimeLocationService,
		public companyService: CompanyService
	) {
		this.searchUser.pipe(
			debounceTime(400),
			distinctUntilChanged())
			.subscribe(value => {
				this.filterUsersToShow(value)
			})
	 }

	public ngOnInit(): void {
		this.getUsersWithLocationRealTime()

		this.intervalLocation = setInterval(() => {
			this.getUsersWithLocationRealTime()
		}, 60000)

		this.fullScreenChangeHandler = this.onFullScreenChange.bind(this)
		document.addEventListener('fullscreenchange', this.fullScreenChangeHandler)
	}

	public ngOnDestroy(): void {
		clearInterval(this.intervalLocation)

		document.removeEventListener('fullscreenchange', this.fullScreenChangeHandler)
	}

	public async getUsersWithLocationRealTime(): Promise<void> {
		try {
			this.usersWithLocationRealtime =
				await this.realtimeLocationService.getLocationRealtimeUsers(this.companyId).toPromise()

			if (this.usersWithLocationRealtime.length === 0) {
				this.hasUsersWithLocationRealTime = false
				console.log('Nenhum usuário com localização em tempo real')
				return
			}

			this.usuariosLogados = this.usersWithLocationRealtime.filter(user => user.internetConnection === 'ONLINE').length

			if (this.map === null) {
				// Initialize Map
				this.address.lat = this.usersWithLocationRealtime[0].locationRealTime.location.lat
				this.address.lng = this.usersWithLocationRealtime[0].locationRealTime.location.lng

				console.log('Initialize Map')
				setTimeout(() => {
					this.initMap()
				}, 1000)
			} else {
				// Auto update cron

				console.log('Auto update cron')
				this.removeMarkers()
				this.insertUsersWithLocationInMap()

				if (this.selectedData) {
					const selectedDataUpdated = this.usersWithLocationRealtime.find(user => user._id === this.selectedData._id)
					this.selectUser(selectedDataUpdated, 'cron')
				}
			}
		} catch (error) {
			console.log(error)
		}
	}

	private removeMarkers() {
		this.markers.forEach(marker => {
			marker.setMap(null)
		})

		this.markers = []
	}

	private initMap() {
		const mapView = document.getElementById('map')

		const map: google.maps.Map = new google.maps.Map(mapView, {
			zoom: 16,
			center: new google.maps.LatLng(this.address.lat, this.address.lng),
			gestureHandling: 'cooperative'
		})

		this.map = map

		map.setOptions({
			styles: [
				{
					featureType: 'poi',
					stylers: [
						{ visibility: 'off' }
					]
				}
			]
		})

		this.insertUsersWithLocationInMap()
	}

	public filterUsersToShow(filteredValue: string) {
		if (filteredValue == 'clear' || filteredValue == '') {
			this.searchUserValue = ''
			this.filteredUsers = []
			this.searchedUserNotFound = false
			return
		}

	 this.filteredUsers = this.usersWithLocationRealtime.filter(user => {
			const email = user.email ? user.email.toLowerCase() : ''
			const cpf = user.cpf ? user.cpf.toString() : ''
			const crea = user.crea ? user.crea.toLowerCase() : ''
			const name = user.name ? user.name.toLowerCase() : ''
			const searchLower = filteredValue.toLowerCase()

			return email.includes(searchLower) || cpf.includes(searchLower) || crea.includes(searchLower) || name.includes(searchLower)
		}).sort((a, b) => {
			if (a.internetConnection === 'ONLINE' && b.internetConnection !== 'ONLINE') {
				return -1
			}
		})

		// Verifica se encontrou usuários
	 this.searchedUserNotFound = this.filteredUsers.length === 0 && filteredValue.length > 0
	}

	public redirectMapToUser(userSelected: UserLocationRealTime) {
		if (!userSelected) {
			console.log('Usuário não encontrado')
			this.searchedUserNotFound = true
			this.closeSelectedData()
			return
		}

		this.filteredUsers = []
		this.searchedUserNotFound = false

		const positionMap = new google.maps.LatLng(
			userSelected.locationRealTime.location.lat,
			userSelected.locationRealTime.location.lng
		)

		this.map.setCenter(positionMap)
		this.map.setZoom(22)

		this.selectUser(userSelected, 'search')
	}

	private insertUsersWithLocationInMap() {
		this.usersWithLocationRealtime.forEach(userWithLocationRealtime => {
			const positionMap = new google.maps.LatLng(
				userWithLocationRealtime.locationRealTime.location.lat,
				userWithLocationRealtime.locationRealTime.location.lng
			)

			const icon = `${this.iconBaseFolder}pin-realtime-legend-evaluator.png`

			this.insertMarker(positionMap, icon, this.map, userWithLocationRealtime)
		})
	}

	private insertMarker(position: google.maps.LatLng, icon: string, map: google.maps.Map, userData: UserLocationRealTime) {
		const marker: google.maps.Marker = new google.maps.Marker({
			position,
			icon,
			map
		})

		marker.addListener(
			'click',
			this.selectUser.bind(
				this,
				userData,
				'click'
			)
		)

		this.markers.push(marker)

		let infowindow: google.maps.InfoWindow

		google.maps.event.addListener(marker, 'mouseover', () => {
			const name = userData.name
			const roles = userData.roles

			infowindow = new google.maps.InfoWindow({
				content: `
					<p>Nome: ${name}</p>
					<br>
					<p>Tipo de usuário:</p>
					<br>
					${roles.map(role => `<p>${role}</p>`).join('')}
				`
			})

			infowindow.open(map, marker)

			$('.gm-style-iw').next('div').hide()
		})

		google.maps.event.addListener(marker, 'mouseout', function() {
			infowindow.close()
		})
	}

	private setAddress(lat: number, lng: number) {
		const geocoder: google.maps.Geocoder = new google.maps.Geocoder()

		const latLng = new google.maps.LatLng(lat, lng)

		try {
			this.addressSelectedUser = '...'
			console.log('Buscando endereço')
			geocoder.geocode({ location: latLng }, (results, status) => {
				if (status === google.maps.GeocoderStatus.OK) {
					if (results[0]) {
						this.addressSelectedUser = results[0].formatted_address
					}
				}
			})
		} catch (error) {
			console.log(error)
			this.addressSelectedUser = 'Erro ao buscar endereço'
		}
	}

	private closeSelectedData() {
		this.selectedData = null

		if (document.fullscreenElement) {
			this.fullScreenUserInformation('close')
		}
	}

	private selectUser(userSelected: UserLocationRealTime, action: 'click' | 'cron' | 'search') {
		if (this.selectedData && this.selectedData._id === userSelected._id && action === 'click') {
			this.closeSelectedData()
			return
		}

		if (userSelected.information &&
			(userSelected.information.lat != this.lastAddresses.lat
				&& userSelected.information.lng != this.lastAddresses.lng)) {
			// Só buscar um novo endereço se a lat e lng forem diferentes para evitar de utilizar
			// o serviço da google a cada refresh

			this.lastAddresses = {
				lat: userSelected.information.lat,
				lng: userSelected.information.lng
			}

			this.setAddress(userSelected.information.lat, userSelected.information.lng)
		}

		this.selectedData = userSelected

		if (document.fullscreenElement) {
			setTimeout(() => {
				this.fullScreenUserInformation('open')
			}, 500)
		}
	}

	public onFullScreenChange() {
		if (document.fullscreenElement && this.selectedData) {
			this.fullScreenUserInformation('open')
		} else {
			this.fullScreenUserInformation('close')
			this.selectedData = null
		}
	}

	public fullScreenUserInformation(action: 'open' | 'close') {
		const userInformation: HTMLDivElement = document.getElementById('userData') as HTMLDivElement

		if (document.fullscreenElement && userInformation) {
			if (action === 'close') {
				userInformation.style.display = 'none'
				document.fullscreenElement.removeChild(userInformation)
				userInformation.remove()
		  return
			}

			userInformation.style.display = 'block'
			userInformation.style.position = 'fixed'
			userInformation.style.right = '0'
			userInformation.style.bottom = '0'
			userInformation.style.height = '95%'
			userInformation.style.width = '30%'
			userInformation.style.zIndex = '9999'

			document.fullscreenElement.appendChild(userInformation)
		} else if (action === 'close' && userInformation) {
			userInformation.remove()
		}
	}
}
